import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { getAsilamaTipleri, addAsilama, updateAsilama } from '../services/api';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Stack, CircularProgress, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './FormStyles.css';

const parseDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date && !isNaN(dateInput)) {
        return dateInput;
    }
    let date;
    if (typeof dateInput === 'object' && dateInput !== null && dateInput.year && dateInput.month && dateInput.day) {
        date = new Date(Date.UTC(dateInput.year, dateInput.month - 1, dateInput.day));
    } else {
        date = new Date(dateInput);
    }
    return !isNaN(date.getTime()) ? date : null;
};

const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const AsilamaForm = ({ open, onClose, onSave, kumesId, donemId, asilamaData }) => {
    // KDV ile ilgili alanlar state'ten kaldırıldı.
    const initialState = {
        ASILAMA_ID: '',
        TARIH: formatDateForInput(new Date()),
        SAAT: new Date().toTimeString().slice(0, 5),
        VETERINER_NOTU: '',
        TOPLAM: '', // Artık bu alan genel toplamı ifade ediyor.
    };

    const [formData, setFormData] = useState(initialState);
    const [asilamaTipleri, setAsilamaTipleri] = useState([]);
    const [loading, setLoading] = useState(false);
    // uiTotals state'i kaldırıldı.
    const { enqueueSnackbar } = useSnackbar();
    const isEditMode = Boolean(asilamaData);

    useEffect(() => {
        if (!open) return;
        const loadAsilamaTipleri = async () => {
            if (!kumesId) return;
            setLoading(true);
            try {
                const res = await getAsilamaTipleri(kumesId);
                setAsilamaTipleri(res.data.data || []);
            } catch (err) {
                enqueueSnackbar('Aşı tipleri yüklenemedi.', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };
        loadAsilamaTipleri();
        
        if (isEditMode && asilamaData) {
            const validDate = parseDate(asilamaData.ZAMAN || asilamaData.TARIH);
            setFormData({
                ASILAMA_ID: asilamaData.ASILAMA_ID || '',
                TARIH: validDate ? formatDateForInput(validDate) : initialState.TARIH,
                SAAT: validDate ? validDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : initialState.SAAT,
                VETERINER_NOTU: asilamaData.VETERINER_NOTU || '',
                // GENEL_TOPLAM alanı artık formdaki TOPLAM alanına set ediliyor
                TOPLAM: asilamaData.GENEL_TOPLAM || '',
                // KDV_ORAN state'ten kaldırıldı.
            });
        } else {
            setFormData(initialState);
        }
    }, [open, kumesId, asilamaData, isEditMode]);

    // KDV hesaplama useEffect'i kaldırıldı.

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            DONEM_ID: donemId,
            ASILAMA_ID: formData.ASILAMA_ID,
            TARIH: formData.TARIH.split('-').reverse().join('.'),
            SAAT: formData.SAAT,
            VETERINER_NOTU: formData.VETERINER_NOTU,
            // KDV_ORAN sunucuya her zaman 0 olarak gönderiliyor.
            KDV_ORAN: 0,
            TOPLAM: Number(formData.TOPLAM) || 0,
        };
        setLoading(true);
        try {
            if (isEditMode) {
                payload.DONEM_ASILAMA_ID = asilamaData.DONEM_ASILAMA_ID;
                await updateAsilama(payload);
                enqueueSnackbar('Aşılama başarıyla güncellendi.', { variant: 'success' });
            } else {
                await addAsilama(payload);
                enqueueSnackbar('Aşılama başarıyla eklendi.', { variant: 'success' });
            }
            onSave();
        } catch (err) {
            enqueueSnackbar(`İşlem başarısız: ${err.message}`, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {isEditMode ? 'Aşılama Düzenle' : 'Yeni Aşılama Ekle'}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: '#FF7A2B' }} /></Box> : (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <div className="form-section">
                                <h4 className="form-section-header">Aşı Bilgileri</h4>
                                <Stack spacing={2.5}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Aşı Tipi</InputLabel>
                                        <Select name="ASILAMA_ID" value={formData.ASILAMA_ID} label="Aşı Tipi" onChange={handleChange}>
                                            <MenuItem value="" disabled>Seçim yapınız...</MenuItem>
                                            {asilamaTipleri.map(asi => <MenuItem key={asi.ASILAMA_ID} value={asi.ASILAMA_ID}>{asi.ASILAMA_ADI}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField label="Tarih" type="date" name="TARIH" value={formData.TARIH} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
                                    <TextField label="Saat" type="time" name="SAAT" value={formData.SAAT} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
                                </Stack>
                            </div>
                            <div className="form-section">
                                <h4 className="form-section-header">Finansal Bilgiler ve Notlar</h4>
                                <Stack spacing={2.5}>
                                    <TextField label="Not" name="VETERINER_NOTU" value={formData.VETERINER_NOTU} onChange={handleChange} multiline rows={3} fullWidth />
                                    {/* KDV alanları kaldırıldı, sadece Toplam Tutar kaldı */}
                                    <TextField label="Toplam Tutar (₺)" type="number" name="TOPLAM" value={formData.TOPLAM} onChange={handleChange} inputProps={{ step: "any" }} required fullWidth />
                                </Stack>
                            </div>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={onClose}>İptal</Button>
                    <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#FF7A2B', '&:hover': { backgroundColor: '#E66A20' } }}>
                        {isEditMode ? 'Güncelle' : 'Yeni Aşı Ekle'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default AsilamaForm;